// State Transition Rules for AcquireOS MVP
// Defines validation rules for module progression and state changes

import { MODULES } from './moduleTransitions.js';
import { validateDealState } from './dealStateSchema.js';

export const MIN_LENDER_MATCH_SCORE = 70;

export class StateTransitionEngine {
  
  // Check if a module can be entered based on current deal state
  static canEnterModule(moduleNumber, dealState) {
    const module = MODULES[moduleNumber];
    if (!module) {
      return { canEnter: false, reason: "Invalid module number" };
    }
    
    // Check if all required fields from previous modules are present
    for (const requiredField of module.requiredFields) {
      if (!this.hasRequiredField(dealState, requiredField)) {
        return { 
          canEnter: false, 
          reason: `Missing required field: ${requiredField}` 
        };
      }
    }
    
    // Specific module transition rules
    switch (moduleNumber) {
      case 1:
        // Module 1 can always be entered (entry point)
        return { canEnter: true };
        
      case 2:
        // Module 2 (Market Intelligence) is always accessible — it is header-pinned
        // and must never gate any module progression.
        return { canEnter: true };
        
      case 3:
        // Module 3 is intentionally open so users can enter deal financials manually.
        return { canEnter: true };
        
      case 4:
        if (!dealState.financialDocuments?.omUploaded) {
          return {
            canEnter: false,
            reason: "Offering Memorandum (OM) must be uploaded in Module 3 before underwriting"
          };
        }
        return { canEnter: true };
        
      case 5:
        // Cannot enter Module 5 unless Module 4 is complete
        if (!this.isModuleComplete(4, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 4 (Underwriting) must be completed first" 
          };
        }
        if (!dealState.underwritingMetrics?.capRate?.entry || !dealState.underwritingMetrics?.dscr?.current) {
          return { 
            canEnter: false, 
            reason: "Underwriting metrics must be calculated in Module 4" 
          };
        }
        break;
        
      case 6:
        // Cannot enter Module 6 unless Module 5 is complete
        if (!this.isModuleComplete(5, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 5 (Deal Score) must be completed first" 
          };
        }
        if (!dealState.scoreAndOffer?.numericalScore || !dealState.scoreAndOffer?.verdict) {
          return { 
            canEnter: false, 
            reason: "Deal score and verdict must be generated in Module 5" 
          };
        }
        if (Number(dealState.scoreAndOffer.numericalScore) <= MIN_LENDER_MATCH_SCORE) {
          return {
            canEnter: false,
            reason: `Deal score must be greater than ${MIN_LENDER_MATCH_SCORE}/100 to enter Module 6 (Lender Match Engine)`
          };
        }
        break;
        
      case 7:
        // Cannot enter Module 7 unless Module 6 is complete
        if (!this.isModuleComplete(6, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 6 (Lender Matching) must be completed first" 
          };
        }
        if (!dealState.lenderMatches?.matchedLenders?.length) {
          return { 
            canEnter: false, 
            reason: "Lender matches must be identified in Module 6" 
          };
        }
        break;
        
      case 8:
        // Cannot enter Module 8 unless Module 7 is complete
        if (!this.isModuleComplete(7, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 7 (Deal Syndication) must be completed first" 
          };
        }
        break;
        
      case 9:
        // Cannot enter Module 9 unless Module 8 is complete
        if (!this.isModuleComplete(8, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 8 (Loan Package) must be completed first" 
          };
        }
        if (!dealState.loanPackage?.documentId) {
          return { 
            canEnter: false, 
            reason: "Loan package must be generated in Module 8" 
          };
        }
        break;
        
      case 10:
        // Cannot enter Module 10 unless Module 9 is complete
        if (!this.isModuleComplete(9, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 9 (Outreach) must be completed first" 
          };
        }
        if (!dealState.outreachMaterials?.emailTemplates?.length) {
          return { 
            canEnter: false, 
            reason: "Communication templates must be generated in Module 9" 
          };
        }
        break;
        
      case 11:
        // Cannot enter Module 11 unless Module 10 is complete
        if (!this.isModuleComplete(10, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 10 (Actions) must be completed first" 
          };
        }
        if (!dealState.actionPlan?.tasks?.length) {
          return { 
            canEnter: false, 
            reason: "Action plan must be created in Module 10" 
          };
        }
        break;
        
      case 12:
        // Cannot enter Module 12 unless Module 11 is complete
        if (!this.isModuleComplete(11, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 11 (LOI) must be completed first" 
          };
        }
        if (!dealState.loiDocument?.documentId) {
          return { 
            canEnter: false, 
            reason: "LOI document must be generated in Module 11" 
          };
        }
        break;
        
      case 13:
        // Cannot enter Module 13 unless Module 12 is complete
        if (!this.isModuleComplete(12, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 12 (PSA) must be completed first" 
          };
        }
        if (!dealState.psaDocument?.documentId) {
          return { 
            canEnter: false, 
            reason: "PSA document must be generated in Module 12" 
          };
        }
        break;
        
      case 14:
        // Cannot enter Module 14 unless Module 13 is complete
        if (!this.isModuleComplete(13, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 13 (Escrow) must be completed first" 
          };
        }
        if (!dealState.escrowDetails?.escrowNumber) {
          return { 
            canEnter: false, 
            reason: "Escrow must be opened in Module 13" 
          };
        }
        break;
        
      case 15:
        // Cannot enter Module 15 unless Module 14 is complete
        if (!this.isModuleComplete(14, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 14 (Due Diligence) must be completed first" 
          };
        }
        if (!dealState.dueDiligence?.finalClearanceConfirmed) {
          return { 
            canEnter: false, 
            reason: "Due diligence must be completed in Module 14" 
          };
        }
        break;
        
      case 16:
        // Cannot enter Module 16 unless Module 15 is complete
        if (!this.isModuleComplete(15, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 15 (Closing) must be completed first" 
          };
        }
        if (!dealState.closingDetails?.recording?.deedRegistered) {
          return { 
            canEnter: false, 
            reason: "Closing must be completed in Module 15" 
          };
        }
        break;
        
      case 17:
        // Cannot enter Module 17 unless Module 16 is complete
        if (!this.isModuleComplete(16, dealState)) {
          return { 
            canEnter: false, 
            reason: "Module 16 (Asset Management) must be completed first" 
          };
        }
        if (!dealState.assetManagement?.kpiDashboard?.activatedAt) {
          return { 
            canEnter: false, 
            reason: "Asset management must be activated in Module 16" 
          };
        }
        break;
    }
    
    return { canEnter: true };
  }
  
  // Check if a module is complete based on deal state
  static isModuleComplete(moduleNumber, dealState) {
    const validation = validateDealState(dealState, moduleNumber);
    return validation.isValid;
  }
  
  // Get the next available module that can be entered
  static getNextAvailableModule(dealState) {
    for (let i = 1; i <= 17; i++) {
      const canEnter = this.canEnterModule(i, dealState);
      if (canEnter.canEnter && !this.isModuleComplete(i, dealState)) {
        return i;
      }
    }
    return null; // All modules complete
  }
  
  // Get all available modules that can be entered
  static getAvailableModules(dealState) {
    const availableModules = [];
    for (let i = 1; i <= 17; i++) {
      const canEnter = this.canEnterModule(i, dealState);
      if (canEnter.canEnter) {
        availableModules.push({
          moduleNumber: i,
          name: MODULES[i].name,
          isComplete: this.isModuleComplete(i, dealState),
          reason: canEnter.reason
        });
      }
    }
    return availableModules;
  }
  
  // Check if pipeline can advance to next stage
  static canAdvancePipeline(dealState) {
    const currentStage = dealState.pipelineStage;
    const nextStage = currentStage + 1;
    
    if (nextStage > 16) {
      return { canAdvance: false, reason: "Already at final stage" };
    }
    
    // Current stage must be complete to advance
    if (!this.isModuleComplete(currentStage, dealState)) {
      return { 
        canAdvance: false, 
        reason: `Current stage (${MODULES[currentStage].name}) must be completed first` 
      };
    }
    
    // Next stage must be enterable
    const canEnterNext = this.canEnterModule(nextStage, dealState);
    if (!canEnterNext.canEnter) {
      return { canAdvance: false, reason: canEnterNext.reason };
    }
    
    return { canAdvance: true };
  }
  
  // Helper function to check if a required field exists in deal state
  static hasRequiredField(dealState, fieldPath) {
    const pathParts = fieldPath.split('.');
    let current = dealState;
    
    for (const part of pathParts) {
      if (current === null || current === undefined) {
        return false;
      }
      current = current[part];
    }
    
    return current !== null && current !== undefined;
  }
  
  // Validate entire deal state for consistency
  static validateDealStateConsistency(dealState) {
    const issues = [];
    
    // Check pipeline stage consistency
    if (dealState.pipelineStage < 1 || dealState.pipelineStage > 16) {
      issues.push("Pipeline stage must be between 1 and 16");
    }
    
    // Check if completed modules align with pipeline stage
    for (let i = 1; i < dealState.pipelineStage; i++) {
      if (!this.isModuleComplete(i, dealState)) {
        issues.push(`Module ${i} should be complete before pipeline stage ${dealState.pipelineStage}`);
      }
    }
    
    // Check financial consistency
    if (dealState.extractedFinancials?.revenue?.totalRevenue && 
        dealState.extractedFinancials?.expenses?.totalExpenses && 
        dealState.extractedFinancials?.noi) {
      const calculatedNOI = dealState.extractedFinancials.revenue.totalRevenue - 
                           dealState.extractedFinancials.expenses.totalExpenses;
      if (Math.abs(calculatedNOI - dealState.extractedFinancials.noi) > 100) {
        issues.push("NOI calculation inconsistency detected");
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  }
}
